/**
 * RTL (Right-to-Left) Utilities
 * Helpers for handling RTL languages like Hebrew and Arabic
 */

/**
 * List of RTL language codes
 */
const RTL_LANGUAGES = ['he', 'ar', 'fa', 'ur', 'yi'];

/**
 * Check if a language code is RTL
 * @param langCode - ISO 639-1 language code
 */
export function isRtlLanguage(langCode: string): boolean {
  return RTL_LANGUAGES.includes(langCode.toLowerCase());
}

/**
 * Get CSS direction value for a language
 * @param langCode - ISO 639-1 language code
 */
export function getDirection(langCode: string): 'ltr' | 'rtl' {
  return isRtlLanguage(langCode) ? 'rtl' : 'ltr';
}

/**
 * Apply RTL transformation to document
 * Updates document direction and lang attributes
 * @param langCode - ISO 639-1 language code
 * @param isRtlOverride - Optional override for RTL detection (from API)
 */
export function applyDocumentDirection(langCode: string, isRtlOverride?: boolean): void {
  const direction = isRtlOverride !== undefined 
    ? (isRtlOverride ? 'rtl' : 'ltr')
    : getDirection(langCode);
  document.documentElement.dir = direction;
  document.documentElement.lang = langCode;

  // Add/remove direction classes for additional styling hooks
  if (direction === 'rtl') {
    document.documentElement.classList.add('rtl');
    document.documentElement.classList.remove('ltr');
  } else {
    document.documentElement.classList.add('ltr');
    document.documentElement.classList.remove('rtl');
  }
}

/**
 * Get the logical direction for flexbox
 * Returns 'row-reverse' for RTL layouts
 * @param isRtl - Whether current language is RTL
 */
export function getFlexDirection(isRtl: boolean): 'row' | 'row-reverse' {
  return isRtl ? 'row-reverse' : 'row';
}

/**
 * Get the start position based on direction
 * Returns 'right' for RTL, 'left' for LTR
 * @param isRtl - Whether current language is RTL
 */
export function getStart(isRtl: boolean): 'left' | 'right' {
  return isRtl ? 'right' : 'left';
}

/**
 * Get the end position based on direction
 * Returns 'left' for RTL, 'right' for LTR
 * @param isRtl - Whether current language is RTL
 */
export function getEnd(isRtl: boolean): 'left' | 'right' {
  return isRtl ? 'left' : 'right';
}

/**
 * Get text alignment class based on direction
 * @param isRtl - Whether current language is RTL
 * @param align - Logical alignment ('start' or 'end')
 */
export function getTextAlign(isRtl: boolean, align: 'start' | 'end' = 'start'): 'text-left' | 'text-right' {
  if (align === 'start') {
    return isRtl ? 'text-right' : 'text-left';
  }
  return isRtl ? 'text-left' : 'text-right';
}

/**
 * Get margin/padding class for inline start
 * @param isRtl - Whether current language is RTL
 * @param size - Tailwind size (e.g., '2', '4', '8')
 * @param property - 'margin' or 'padding'
 */
export function getInlineStart(
  isRtl: boolean,
  size: string,
  property: 'margin' | 'padding' = 'margin'
): string {
  const prefix = property === 'margin' ? 'm' : 'p';
  const side = isRtl ? 'r' : 'l';
  return `${prefix}${side}-${size}`;
}

/**
 * Get margin/padding class for inline end
 * @param isRtl - Whether current language is RTL
 * @param size - Tailwind size (e.g., '2', '4', '8')
 * @param property - 'margin' or 'padding'
 */
export function getInlineEnd(
  isRtl: boolean,
  size: string,
  property: 'margin' | 'padding' = 'margin'
): string {
  const prefix = property === 'margin' ? 'm' : 'p';
  const side = isRtl ? 'l' : 'r';
  return `${prefix}${side}-${size}`;
}
